/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CacheType, createObservableDataAction, getCatalogId, IAction, IActionContext, IActionInput } from '@msdyn365-commerce/core';
import { ProductPrice, ProductsDataActions, ProductSearchResult, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';

//mmx-feature: --start
/*
import { ArrayExtensions } from './utilities';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';
*/
import { ArrayExtensions, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
//mmx-feature: --end

/**
 * Input class for the GetPriceForProductSearchResult Data Action.
 */
export class PriceForProductSearchResultInput implements IActionInput {
    public products: ProductSearchResult[];
    //mmx-feature: --start
    public customerId?: string;

    /*
    public constructor(products: ProductSearchResult[]) {
        this.products = products;
    }
    */
    public constructor(products: ProductSearchResult[], customerId?: string) {
        this.products = products;
        this.customerId = customerId;
    }
    //mmx-feature: --end

    /**
     * Get the cache key.
     * @returns The cache key.
     */
    public getCacheKey = (): string => `PriceForProductSearchResult-${JSON.stringify(this.products)}`;

    /**
     * Get the cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'ProductSearchResult';

    /**
     * Define the data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetPriceForProductSearchResultDataAction.
 * @param products -- The product search result.
 * @returns The input.
 */
export const createActivePriceForProductSearchResultInput = (products: ProductSearchResult[]): PriceForProductSearchResultInput => {
    return new PriceForProductSearchResultInput(products);
};

/**
 * The Action Method for the GetPriceForProductSearchResult Data Action
 * Gets the active price for product search result via the getActivePrice RetailServer API.
 * @param input -- The data input.
 * @param context -- The action context.
 * @returns The product search result updated with price.
 */
export async function getPriceForProductSearchResultAction(
    input: PriceForProductSearchResultInput,
    context: IActionContext
): Promise<ProductSearchResult[]> {
    let productRecIds: number[];
    if (ArrayExtensions.hasElements(input.products)) {
        productRecIds = input.products.map(product => product.RecordId);
    } else {
        return Promise.resolve([]);
    }

    const catalogId = getCatalogId(context.requestContext);

    const projectDomain: ProjectionDomain = { ChannelId: +context.requestContext.apiSettings.channelId, CatalogId: catalogId };
    const priceZero = 0;

    try {
        const productPrices: ProductPrice[] = await ProductsDataActions.getActivePricesAsync(
            { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            undefined,
            true
        );

        const productResults: ProductSearchResult[] = input.products.map(product => {
            const activePrice = productPrices.find(price => price.ProductId === product.RecordId);
            //mmx-feature: discount fix --start
            product.ExtensionProperties?.push({ Key: 'BasePrice', Value: { IntegerValue: activePrice?.BasePrice } });
            product.ExtensionProperties?.push({ Key: 'AdjustedPrice', Value: { IntegerValue: activePrice?.AdjustedPrice } });
            product.ExtensionProperties?.push({
                Key: 'CustomerContextualPrice',
                Value: { IntegerValue: activePrice?.CustomerContextualPrice }
            });
            //mmx-feature: discount fix --end
            if (activePrice) {
                product.Price = activePrice.CustomerContextualPrice ?? product.Price;
                product.BasePrice = Math.max(
                    activePrice.BasePrice ?? priceZero,
                    activePrice.TradeAgreementPrice ?? priceZero,
                    activePrice.AdjustedPrice ?? priceZero
                );
            }
            return product;
        });

        return Promise.resolve(productResults);
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- existing error type.
        //@ts-ignore
        context.telemetry.exception(error);
        context.telemetry.debug('[getPriceForProductSearchResultAction]Error executing action');
        throw new Error('[getPriceForProductSearchResultAction]Error executing action');
    }
}

/**
 * The getPriceForProductSearchResult data action.
 */
export const getPriceForProductSearchResultDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-price-for-product-search-result',
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- existing pattern.
    action: <IAction<ProductSearchResult[]>>getPriceForProductSearchResultAction,
    input: createActivePriceForProductSearchResultInput
});

export default getPriceForProductSearchResultDataAction;
